import { useEffect, useState } from "react";
import { UserPlus, Plus, X, Share2, Trash2, Send, User, Loader2 } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { PageTitle, PageSubtitle, Loader, EmptyState, Card, BackHeader } from "@/components/common";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function when(v) {
  return v ? new Date(v).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
}
const EMPTY_MISSION = { date: "", time: "", pickup_address: "", delivery_address: "", customer: "", reference: "", tractor_plate: "", trailer_plate: "", description: "", notes: "" };

export default function DriversPage() {
  const [drivers, setDrivers] = useState([]);
  const [missions, setMissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [missionDriver, setMissionDriver] = useState(null);
  const [editingMission, setEditingMission] = useState(null);
  const [missionForm, setMissionForm] = useState(EMPTY_MISSION);
  const [missionSaving, setMissionSaving] = useState(false);
  const [missionError, setMissionError] = useState("");

  const load = () => {
    setLoading(true);
    apiFetch("/drivers")
      .then(async (rows) => {
        setDrivers(rows);
        const linked = rows.filter((d) => d.driver_user_id);
        const entries = await Promise.all(linked.map(async (d) => [d.id, await apiFetch(`/drivers/${d.id}/missions`).catch(() => [])]));
        setMissions(Object.fromEntries(entries));
      })
      .catch((e) => setError(e.message || "Errore di caricamento."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim()) { setError("Inserisci il nome dell'autista."); return; }
    setSaving(true);
    setError("");
    try {
      const d = await apiFetch("/drivers", { method: "POST", body: JSON.stringify({ name: name.trim(), phone: phone.trim() }) });
      setDrivers((old) => [d, ...old]);
      setShowForm(false);
      setName("");
      setPhone("");
    } catch (e) {
      setError(e.message || "Riprova.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await apiFetch(`/drivers/${id}`, { method: "DELETE" });
      setDrivers((old) => old.filter((d) => d.id !== id));
    } catch (e) {
      setError(e.message || "Riprova.");
    }
  };

  const shareCode = (d) => {
    const msg = `CargoFlow: ciao ${d.name}, collegati come autista con il codice invito ${d.invite_code}`;
    if (navigator.share) navigator.share({ text: msg }).catch(() => {});
    else { navigator.clipboard?.writeText(d.invite_code); window.alert(`Codice invito copiato: ${d.invite_code}`); }
  };

  const openMission = (d, m) => {
    setMissionDriver(d);
    setEditingMission(m);
    setMissionForm(m ? { ...EMPTY_MISSION, ...m } : EMPTY_MISSION);
    setMissionError("");
  };

  const saveMission = async () => {
    if (!missionForm.date.trim() || !missionForm.pickup_address.trim() || !missionForm.delivery_address.trim()) {
      setMissionError("Data, indirizzo di carico e scarico sono obbligatori.");
      return;
    }
    setMissionSaving(true);
    setMissionError("");
    try {
      if (editingMission) {
        await apiFetch(`/missions/${editingMission.id}`, { method: "PATCH", body: JSON.stringify(missionForm) });
      } else {
        await apiFetch(`/drivers/${missionDriver.id}/missions`, { method: "POST", body: JSON.stringify(missionForm) });
      }
      setMissionDriver(null);
      setEditingMission(null);
      load();
    } catch (e) {
      setMissionError(e.message || "Riprova.");
    } finally {
      setMissionSaving(false);
    }
  };

  const removeMission = async (driverId, m) => {
    if (!window.confirm(`Eliminare la missione ${m.date} · ${m.pickup_address} → ${m.delivery_address}?`)) return;
    try {
      await apiFetch(`/missions/${m.id}`, { method: "DELETE" });
      setMissions((old) => ({ ...old, [driverId]: (old[driverId] ?? []).filter((it) => it.id !== m.id) }));
    } catch (e) {
      window.alert(e.message || "Riprova.");
    }
  };

  const inputCls = "min-h-[50px] w-full rounded-xl border border-border bg-card px-3 text-[15px] outline-none focus:border-primary";
  const label = "mt-2 block text-xs font-bold text-card-foreground";
  const mLabel = "mt-2 block text-xs font-bold text-card-foreground";

  return (
    <div className="space-y-3">
      <BackHeader />
      <div className="flex items-start justify-between">
        <div>
          <PageTitle>Autisti</PageTitle>
          <PageSubtitle>Ogni autista riceve un codice invito per collegarsi alla tua flotta.</PageSubtitle>
        </div>
        <button data-testid="driver-add-button" onClick={() => setShowForm((v) => !v)} className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
          {showForm ? <X className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
        </button>
      </div>

      {showForm ? (
        <Card>
          <p className="mb-1 text-lg font-extrabold text-foreground">Nuovo autista</p>
          <label className={label}>Nome e cognome</label>
          <input data-testid="driver-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Mario Rossi" className={inputCls} />
          <label className={label}>Telefono (facoltativo)</label>
          <input data-testid="driver-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+39 333 1234567" className={inputCls} />
          <p className="mt-2 text-xs text-muted-foreground">Alla creazione viene generato un codice invito da condividere con l'autista.</p>
          <button data-testid="driver-save-button" onClick={add} disabled={saving} className="mt-3 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-extrabold text-primary-foreground disabled:opacity-60">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : null} CREA E GENERA CODICE
          </button>
        </Card>
      ) : null}

      {error ? <p data-testid="drivers-error" className="text-center text-sm text-destructive">{error}</p> : null}

      {loading ? (
        <Loader />
      ) : drivers.length === 0 ? (
        <EmptyState icon={UserPlus} title="Nessun autista" text="Aggiungi il primo autista e condividi il suo codice invito." />
      ) : (
        drivers.map((d) => (
          <Card key={d.id} data-testid={`driver-card-${d.id}`} className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary">
                <User className="h-5 w-5 text-primary" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-extrabold text-foreground">{d.name}</p>
                {d.phone ? <p className="truncate text-xs text-muted-foreground">{d.phone}</p> : null}
                <p className="mt-1 text-xs text-muted-foreground">
                  Codice invito: <span data-testid={`driver-code-${d.id}`} className="font-extrabold tracking-widest text-primary">{d.invite_code}</span>
                </p>
                <p className={cn("mt-1 text-[11px] font-bold", d.driver_user_id ? "text-[hsl(var(--success))]" : "text-muted-foreground")}>
                  {d.driver_user_id ? "Collegato" : "In attesa di collegamento"}
                </p>
              </div>
              <button data-testid={`driver-share-${d.id}`} onClick={() => shareCode(d)} className="p-2 text-primary"><Share2 className="h-5 w-5" /></button>
              <button data-testid={`driver-remove-${d.id}`} onClick={() => remove(d.id)} className="p-2 text-destructive"><Trash2 className="h-5 w-5" /></button>
            </div>
            {d.driver_user_id ? (
              <>
                <button data-testid={`driver-mission-open-${d.id}`} onClick={() => openMission(d, null)} className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-extrabold text-primary-foreground">
                  <Send className="h-5 w-5" /> Invia missione
                </button>
                {(missions[d.id] ?? []).slice(0, 3).map((m) => (
                  <div key={m.id} className="flex items-center justify-between gap-2 border-t border-border pt-2.5">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-extrabold text-foreground">{m.pickup_address} → {m.delivery_address}</p>
                      <p className="text-xs text-muted-foreground">{m.date}{m.time ? ` · ${m.time}` : ""}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      <span className={cn("text-[11px] font-extrabold", m.status === "acknowledged" ? "text-[hsl(var(--success))]" : m.status === "seen" ? "text-[hsl(var(--warning))]" : "text-muted-foreground")}>
                        {m.status === "acknowledged" ? `Confermato · ${when(m.acknowledged_at)}` : m.status === "seen" ? `Visto · ${when(m.seen_at)}` : `Inviato · ${when(m.sent_at)}`}
                      </span>
                      <div className="flex gap-1.5">
                        <button data-testid={`mission-edit-${m.id}`} onClick={() => openMission(d, m)} className="rounded-lg border border-border px-2 py-1 text-[11px] font-extrabold text-primary">Modifica</button>
                        <button data-testid={`mission-delete-${m.id}`} onClick={() => removeMission(d.id, m)} className="rounded-lg border border-border px-2 py-1 text-[11px] font-extrabold text-destructive">Elimina</button>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ) : null}
          </Card>
        ))
      )}

      <Dialog open={missionDriver !== null} onOpenChange={(o) => !o && setMissionDriver(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingMission ? "Modifica missione" : `Invia missione a ${missionDriver?.name || ""}`}</DialogTitle>
          </DialogHeader>
          {[
            ["Data *", "date", "Es. 18 ott"],
            ["Orario", "time", "08:30"],
            ["Indirizzo di carico *", "pickup_address", "Via / città"],
            ["Indirizzo di scarico *", "delivery_address", "Via / città"],
            ["Cliente / stabilimento", "customer", ""],
            ["Riferimento", "reference", ""],
            ["Targa trattore", "tractor_plate", ""],
            ["Targa semirimorchio", "trailer_plate", ""],
            ["Descrizione", "description", ""],
            ["Note", "notes", ""],
          ].map(([lab, key, ph]) => (
            <div key={key}>
              <label className={mLabel}>{lab}</label>
              <input value={missionForm[key]} onChange={(e) => setMissionForm({ ...missionForm, [key]: e.target.value })} placeholder={ph} className="min-h-[46px] w-full rounded-xl border border-border bg-secondary/40 px-3 text-[15px] outline-none focus:border-primary" data-testid={`mission-field-${key}`} />
            </div>
          ))}
          {missionError ? <p className="text-sm text-destructive">{missionError}</p> : null}
          <button data-testid="mission-submit" onClick={saveMission} disabled={missionSaving} className="mt-2 flex min-h-[50px] items-center justify-center gap-2 rounded-xl bg-primary text-sm font-extrabold text-primary-foreground disabled:opacity-60">
            {missionSaving ? <Loader2 className="h-5 w-5 animate-spin" /> : null} {editingMission ? "SALVA MODIFICHE" : "INVIA MISSIONE"}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
